<template>
  <div class="received-gift-show-container">
    <div v-if="feedbackNoticeMessage" class="reply-feedback--success feedback">
      <transition name="fade" @enter="enterSuccess">
        <FeedbackSuccess v-if="hasSuccess" :message="feedbackNoticeMessage" />
      </transition>
    </div>
    <div v-if="feedbackErrorMessage" class="reply-feedback--error feedback">
      <transition name="fade" @enter="enterError">
        <FeedbackError v-if="hasError" :message="feedbackErrorMessage" />
      </transition>
    </div>
    <div class="gift-card__body">
      <div
        id="select-card-design-content"
        class="gift-card__card-design-content"
      >
        <ImgLazy
          :image-url="cardDesignImageUrl"
          alt-text="カードデザイン"
          aspect-ratio="1"
          class="gift-card__card-design-image"
        />
      </div>
      <div class="gift-card__content">
        <div class="gift-card__sender-name-area account">
          <div v-if="createdUserProfileIconUrl" class="gift-card__account-icon">
            <AccountIconWh24 :image-url="createdUserProfileIconUrl" />
          </div>
          <div id="sender-name" class="gift-card__sender-name body-bold">
            <a
              :href="showUsableProfileLink ? createdUserProfileUrl : null"
              :class="
                showUsableProfileLink
                  ? 'gift-card__sender-name--link'
                  : 'gift-card__sender-name--no-link'
              "
            >
              {{ senderName }}
            </a>
            <span class="gift-card__sender-name-suffix module-text">
              さんより
            </span>
          </div>
        </div>
        <div v-if="message" class="gift-card__message-block-area">
          <div class="message_block">
            <div class="message--expand">
              {{ message }}
            </div>
          </div>
        </div>
        <div class="gift-card__ticket-list-area">
          <div class="gift-card__gift-outline body-bold">
            ギフトが届いています
          </div>
          <div
            v-for="(ticket, index) in giftCardTickets.data"
            :key="index"
            class="gift-card__ticket-area"
          >
            <TicketWithLink
              :link-url="ticket.attributes.url"
              :image-url="ticket.attributes.item_sku_image"
              :item-name="ticket.attributes.item_name"
              :item-sku-name="ticket.attributes.item_sku_name"
              :brand-name="ticket.attributes.brand_name"
              :expiration-date="
                formatUsableLimitAt(ticket.attributes.usable_limit_at)
              "
              :is-ticket-expired="ticket.attributes.expired"
              :is-ticket-exchanged="ticket.attributes.exchanged"
              :showable-giftee-asterisk="true"
            />
          </div>
          <div class="gift-card__terms-of-service-message">
            <!-- eslint-disable-next-line prettier/prettier 改行によって空白が入らないよう1行にまとめる -->
            ギフトのご利用をもって<a href="/terms_of_service" target="_blank">利用規約</a>へのご同意とみなします。
          </div>
        </div>
      </div>
    </div>
    <NewYear
      v-if="orderId"
      :lottery-number="orderId"
      :token="giftCardToken"
      :created-at="giftCardCreatedAt"
    />
    <!-- ギフトを保存 -->
    <div class="save-gift__area">
      <!-- 未ログイン -->
      <div v-if="!loggedIn">
        <p class="save-gift__caption">使い忘れのないよう保存がおすすめ</p>
        <div class="save-gift__banner">
          <a :href="registrationUrl" @click="onClickSignUpLink">
            <BannerNoLoginSave />
          </a>
        </div>
        <a
          :href="loginUrl"
          class="save-gift__login-link text-link"
          @click="onClickSignInLink"
        >
          ログインして保存する
        </a>
      </div>
      <!-- ログイン済み・自分のギフト -->
      <div
        v-else-if="loggedInCreatedUser"
        class="save-gift__created_user caption"
      >
        <p>ご自身のギフトはもらったギフトに保存することはできません。</p>
        <a :href="orderHistoriesUrl"> 購入履歴からご確認ください。 </a>
      </div>
      <!-- ログイン済み・保存済み -->
      <div v-else-if="loggedInReceivedUser">
        <a
          :href="receivedLogsUrl"
          class="save-gift__received_log_link text-link"
        >
          保存したカードとギフトを見る
        </a>
      </div>
      <!-- ログイン済み・他のユーザーが保存/他のユーザーがお礼メッセージ送信済み -->
      <div
        v-else-if="otherUserSaved"
        class="save-gift__other-user-saved caption"
      >
        このギフトはすでに他のユーザーによって保存されています
      </div>
      <div v-else>
        <p class="save-gift__caption">使い忘れのないよう保存がおすすめ</p>
        <div class="save-gift__banner">
          <a
            :href="updateGiftOwnerUrl"
            data-method="put"
            rel="nofollow"
            @click="onClickSaveLink"
          >
            <BannerLoginSave />
          </a>
        </div>
      </div>
    </div>
    <!-- お礼メッセージを送る -->
    <div v-if="displaySendReplyArea" class="send-gift-card-reply__area">
      <p
        class="text-link--bold send-gift-card-reply__text-link"
        :class="{
          'tl-chevron-up--bold--right': isActive,
          ' tl-chevron-down--bold--right': !isActive
        }"
        @click="showExplanation"
      >
        ありがとうのメッセージを送る
      </p>
      <div v-show="isActive" class="send-gift-card-reply__form">
        <form
          :action="sendReplyPostUrl"
          method="POST"
          accept-charset="UTF-8"
          name="received_gift_form"
        >
          <input name="utf8" type="hidden" value="✓" />
          <input
            type="hidden"
            name="authenticity_token"
            :value="authenticityToken"
          />
          <div
            v-if="sendReplyErrorMessages.length >= 1"
            class="box-list--alert send-gift-card-reply-form__error-message"
          >
            <div
              v-for="(sendReplyErrorMessage, index) in sendReplyErrorMessages"
              :key="index"
            >
              ・{{ sendReplyErrorMessage }}
            </div>
          </div>
          <div class="send-gift-card-reply-form__minimal-message minimal">
            ギフトをくれた人にキモチを伝えてみませんか？（300文字以内）
          </div>
          <div class="send-gift-card-reply-form__content-area">
            <textarea
              id="gift-reply-content"
              v-model="content"
              name="received_gift_form[content]"
              class="textarea--short"
              :class="{
                field_with_errors: hasReplyContentError,
                'input-form--inputted': isNotEmptyContent,
                'input-form': !isNotEmptyContent,
                'input-form--inputting': isFocusContent
              }"
              placeholder="メッセージ..."
              maxlength="300"
              @focus="onContentFocus"
              @blur="onContentBlur"
            />
          </div>
          <div class="send-gift-card-reply-form__sender-area">
            <div class="send-gift-card-reply-form__user-icon">
              <AccountIconWh40 :image-url="currentUserProfileIconUrl" />
            </div>
            <input
              id="gift-reply-sender"
              v-model="sender"
              type="text"
              name="received_gift_form[sender]"
              :class="{
                field_with_errors: hasReplySenderError,
                'input-form--inputted': isNotEmptySender,
                'input-form': !isNotEmptySender,
                'input-form--inputting': isFocusSender
              }"
              placeholder="ニックネーム(20文字以内)"
              maxlength="20"
              @focus="onContentFocus"
              @blur="onContentBlur"
              @keydown.enter.prevent=""
            />
          </div>
          <div class="send-gift-card-reply-form__submit-button-area">
            <input
              type="submit"
              class="btn btn--primary"
              :disabled="!isSubmittable || null"
              data-disable-with="メッセージを送信する"
            />
          </div>
        </form>
      </div>
    </div>
    <!-- お礼メッセージの表示 -->
    <div v-if="hasReply" class="gift-card-reply__area">
      <div class="gift-card-reply__title body-bold">お礼メッセージ</div>
      <div class="gift-card-reply__created_at minimal">
        {{ replyCreatedAt }}
      </div>
      <div class="gift-card-reply__content caption">
        {{ replyContent }}
      </div>
      <div class="gift-card-reply__account">
        <div class="gift-card-reply__sender caption-bold">
          {{ replySender }}
        </div>
        <AccountIconWh24 :image-url="replyUserProfileIconUrl" />
      </div>
    </div>
    <div v-if="!notDisplayAboutGiftee" class="about-giftee__area">
      <div>
        <p class="about-giftee__caption">
          あなたも誰かにキモチを贈ってみませんか？
        </p>
        <div class="about-giftee__banner">
          <a :href="aboutUrl" target="_blank">
            <BannerAboutGiftee />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import TicketWithLink from '../../atoms/tickets/TicketWithLink.vue'
import FeedbackSuccess from '../../atoms/feedbacks/feedback-success.vue'
import FeedbackError from '../../atoms/feedbacks/feedback-error.vue'
import AccountIconWh24 from '../../atoms/account_icons/AccountIconWh24.vue'
import AccountIconWh40 from '../../atoms/account_icons/AccountIconWh40.vue'
import BannerNoLoginSave from '../../atoms/svg_icons/BannerNoLoginSave.vue'
import BannerLoginSave from '../../atoms/svg_icons/BannerLoginSave.vue'
import BannerAboutGiftee from '../../atoms/svg_icons/BannerAboutGiftee.vue'
import NewYear from '../../organism/new_year/NewYear.vue'
import ImgLazy from '../../atoms/images/ImgLazy.vue'

export default {
  components: {
    TicketWithLink,
    FeedbackSuccess,
    FeedbackError,
    AccountIconWh24,
    AccountIconWh40,
    BannerNoLoginSave,
    BannerLoginSave,
    BannerAboutGiftee,
    NewYear,
    ImgLazy
  },
  props: {
    feedbackNoticeMessage: {
      type: String,
      required: false,
      default: ''
    },
    feedbackErrorMessage: {
      type: String,
      required: false,
      default: ''
    },
    cardDesignImageUrl: {
      type: String,
      required: false,
      default: ''
    },
    createdUserProfileIconUrl: {
      type: String,
      required: false,
      default: ''
    },
    showUsableProfileLink: {
      type: Boolean,
      required: false,
      default: false
    },
    createdUserProfileUrl: {
      type: String,
      required: false,
      default: ''
    },
    senderName: {
      type: String,
      required: false,
      default: ''
    },
    message: {
      type: String,
      required: false,
      default: ''
    },
    giftCardTickets: {
      type: Object,
      required: false,
      default: () => {}
    },
    loggedIn: {
      type: Boolean,
      required: false,
      default: false
    },
    registrationUrl: {
      type: String,
      required: false,
      default: '/barista/registrations/new'
    },
    loginUrl: {
      type: String,
      required: false,
      default: '/barista/sessions/new'
    },
    loggedInCreatedUser: {
      type: Boolean,
      required: false,
      default: false
    },
    orderHistoriesUrl: {
      type: String,
      required: false,
      default: '/mypage/order_histories'
    },
    loggedInReceivedUser: {
      type: Boolean,
      required: false,
      default: false
    },
    receivedLogsUrl: {
      type: String,
      required: false,
      default: '/mypage/received_logs'
    },
    otherUserSaved: {
      type: Boolean,
      required: false,
      default: false
    },
    updateGiftOwnerUrl: {
      type: String,
      required: false,
      default: ''
    },
    displaySendReplyArea: {
      type: Boolean,
      required: false,
      default: false
    },
    sendReplyPostUrl: {
      type: String,
      required: false,
      default: ''
    },
    authenticityToken: {
      type: String,
      required: true
    },
    sendReplyErrorMessages: {
      type: Array,
      required: false,
      default: () => []
    },
    initialReplyContent: {
      type: String,
      required: false,
      default: ''
    },
    currentUserProfileIconUrl: {
      type: String,
      required: false,
      default: ''
    },
    initialReplySender: {
      type: String,
      required: false,
      default: ''
    },
    hasReply: {
      type: Boolean,
      required: false,
      default: false
    },
    replyCreatedAt: {
      type: String,
      required: false,
      default: ''
    },
    replyContent: {
      type: String,
      required: false,
      default: ''
    },
    replySender: {
      type: String,
      required: false,
      default: ''
    },
    replyUserProfileIconUrl: {
      type: String,
      required: false,
      default: ''
    },
    notDisplayAboutGiftee: {
      type: Boolean,
      required: false,
      default: false
    },
    aboutUrl: {
      type: String,
      required: false,
      default: '/about'
    },
    hasReplyContentError: {
      type: Boolean,
      required: false,
      default: false
    },
    hasReplySenderError: {
      type: Boolean,
      required: false,
      default: false
    },
    orderId: {
      type: String,
      required: false,
      default: ''
    },
    giftCardToken: {
      type: String,
      required: false,
      default: ''
    },
    giftCardCreatedAt: {
      type: String,
      required: false,
      default: ''
    }
  },
  data() {
    return {
      sender: '',
      content: '',
      isActive: false,
      hasError: false,
      hasSuccess: false,
      showableSignUpModal: false,
      isFocusSender: false,
      isFocusContent: false
    }
  },
  computed: {
    isNotEmptySender() {
      return this.sender.length !== 0
    },
    isNotEmptyContent() {
      return this.content.length !== 0
    },
    isSubmittable() {
      return this.isNotEmptySender && this.isNotEmptyContent
    }
  },
  mounted() {
    if (this.initialReplySender) {
      this.sender = this.initialReplySender
    }

    if (this.initialReplyContent) {
      this.content = this.initialReplyContent
    }

    if (this.feedbackNoticeMessage) {
      this.hasSuccess = true
      this.enterSuccess()
    }

    if (this.feedbackErrorMessage) {
      this.hasError = true
      this.enterError()
    }
  },
  methods: {
    showExplanation() {
      this.isActive = !this.isActive
    },
    onSenderFocus() {
      this.isFocusSender = true
    },
    onSenderBlur() {
      this.isFocusSender = false
    },
    onContentFocus() {
      this.isFocusContent = true
    },
    onContentBlur() {
      this.isFocusContent = false
    },
    enterSuccess() {
      setTimeout(() => {
        this.hasSuccess = false
      }, 3000)
    },
    enterError() {
      setTimeout(() => {
        this.hasError = false
      }, 3000)
    },
    onClickSignUpLink() {
      dataLayer.push({
        event: 'signup_click',
        button: 'gift_save'
      })
    },
    onClickSignInLink() {
      dataLayer.push({
        event: 'signin_click',
        button: 'gift_save'
      })
    },
    onClickSaveLink() {
      dataLayer.push({
        event: 'save_click'
      })
    },
    formatUsableLimitAt(usableLimitAt) {
      const date = new Date(usableLimitAt)
      return `${date.getFullYear()}年${
        date.getMonth() + 1
      }月${date.getDate()}日`
    }
  }
}
</script>
<style lang="scss" scoped>
.received-gift-show-container {
  width: 100%;
  margin: auto;
  @media screen and (min-width: $sp-width) {
    max-width: $small-contents-width;
  }

  .gift-card {
    &__body {
      background-color: $white;
    }

    &__card-design-content {
      width: 100%;
      margin: 0 auto;
      display: flex;
      justify-content: center;
      flex-direction: column;
      @media screen and (min-width: $sp-width) {
        max-width: $small-contents-width;
        max-height: $small-contents-width;
      }
    }

    &__card-design-image {
      width: 100%;
      height: auto;
    }

    &__content {
      padding: 24px 16px;

      @media screen and (max-width: $small-contents-width) {
        // 16px / 375px * 100 = 4.266...
        padding-left: 4.27%;
        padding-right: 4.27%;
      }
    }

    &__sender-name-area {
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      text-align: left;
    }

    &__account-icon {
      padding-right: 8px;
    }

    &__sender-name-suffix {
      font-weight: normal;
    }

    &__sender-name {
      display: flex;

      &--no-link {
        color: $black;
        pointer-events: none;
      }

      &--link {
        color: $link;
      }
    }

    &__message-block-area {
      margin: 8px 0 16px 0;

      text-align: left;
      white-space: pre-wrap;
    }

    &__gift-outline {
      margin: 16px 0 24px 0;
      padding-left: 24px;
      @include bg-settings(
        '../../../../../images/icons/gift-outline.svg',
        $white,
        20px,
        22px
      );

      text-align: left;
    }

    &__ticket-area {
      text-align: left;

      &:not(:last-child) {
        margin-bottom: 16px;
      }
    }

    &__terms-of-service-message {
      @extend %caption;
      margin-top: 24px;
      color: $black;
    }
  }

  .save-gift {
    &__area {
      padding: 24px 16px;
      margin-top: 16px;
      @media screen and (max-width: $small-contents-width) {
        // 16px / 375px * 100 = 4.266...
        padding-left: 4.27%;
        padding-right: 4.27%;
      }

      background-color: $white;
    }

    &__caption {
      @extend %caption-bold;
      color: $black;
    }

    &__banner {
      padding-top: 16px;
    }

    &__login-link {
      margin-top: 24px;
      display: block;
    }

    &__received_log_link {
      display: block;
    }

    &__created_user {
      color: $black;
    }

    &__other-user-saved {
      color: $black;
    }
  }

  .send-gift-card-reply {
    &__area {
      background-color: $white;
      margin-top: 16px;
      padding: 24px 0;
    }

    &__text-link {
      padding: 0 16px;
      display: inline;
    }

    &__form {
      padding: 0 16px;
      margin-top: 24px;
    }
  }

  .send-gift-card-reply-form {
    &__error-message {
      margin-bottom: 16px;
      white-space: pre-wrap;
      text-align: left;
    }

    &__minimal-message {
      text-align: left;
      margin-bottom: 8px;
    }

    &__content-area {
      margin-bottom: 16px;
    }

    &__sender-area {
      display: flex;
      align-items: center;

      .field_with_errors {
        width: 100%;
      }
    }

    &__user-icon {
      margin-right: 8px;
    }

    &__submit-button-area {
      margin: 40px 0 16px 0;

      input[type='submit'] {
        border: none;
      }
    }
  }

  .gift-card-reply {
    &__area {
      background-color: $white;
      margin-top: 16px;
      padding: 16px;
    }

    &__title {
      margin-bottom: 16px;
      padding-left: 24px;
      @include bg-settings(
        '../../../../../images/icons/paper-plane.svg',
        $white,
        20px,
        22px
      );

      text-align: left;
    }

    &__created_at {
      text-align: left;
      margin-bottom: 4px;
    }

    &__content {
      text-align: left;
      white-space: pre-wrap;
      background-color: $gray-3;
      border-radius: 8px;
      color: $black;
      overflow-wrap: break-word;
      margin-bottom: 8px;
      padding: 16px;
      @media screen and (max-width: $small-contents-width) {
        // 16px / 375px * 100 = 4.266...
        padding-left: 4.27%;
        padding-right: 4.27%;
      }
    }

    &__account {
      display: flex;
      justify-content: flex-end;
    }

    &__sender {
      margin: auto 4px auto 0;
      color: $gray-1;
    }
  }

  .about-giftee {
    &__area {
      margin-top: 24px;
      padding: 0 16px;
      @media screen and (max-width: $small-contents-width) {
        // 16px / 375px * 100 = 4.266...
        padding-left: 4.27%;
        padding-right: 4.27%;
      }
    }

    &__caption {
      @extend %caption-bold;
      color: $black;
    }

    &__banner {
      padding-top: 16px;
    }
  }
}
</style>
